import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { scrollToSection } from "@/lib/scroll";
import Logo from "@/components/Logo";

const LINKS = [
  { href: "#value", label: "The Problem" },
  { href: "#resume", label: "Experience" },
  { href: "/services", label: "Services" },
  { href: "#capabilities", label: "Capabilities" },
  { href: "#contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const onHome = location.pathname === "/";
  const goSection = (e, href) => {
    if (!onHome) return;
    e.preventDefault();
    scrollToSection(href);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled
          ? "bg-[#0B0F17]/80 backdrop-blur-xl border-b border-white/[0.07] shadow-[0_8px_40px_rgba(0,0,0,0.35)]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[72px] flex items-center justify-between">
        <Logo />
        <nav className="hidden md:flex items-center gap-8">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={onHome || !l.href.startsWith("#") ? l.href : `/${l.href}`}
              onClick={(e) => !l.href.startsWith("/") && goSection(e, l.href)}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono text-[11px] uppercase tracking-[0.22em] text-[#9CA3AF] hover:text-[#00F0FF] transition-colors duration-300"
            >
              {l.label}
            </a>
          ))}
          <a
            href={onHome ? "#contact" : "/#contact"}
            onClick={(e) => goSection(e, "#contact")}
            data-testid="nav-hire-cta"
            className="font-display text-sm font-semibold text-[#0B0F17] bg-[#00F0FF] hover:bg-[#4DF7FF] rounded-full px-5 py-2.5 transition-all duration-300 hover:-translate-y-0.5 hover:shadow-[0_8px_30px_rgba(0,240,255,0.35)]"
          >
            Hire Me
          </a>
        </nav>
        <button
          data-testid="mobile-menu-button"
          aria-label="Toggle menu"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden h-10 w-10 flex items-center justify-center rounded-lg border border-white/10 bg-white/5 text-[#F9FAFB]"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.25 }}
            className="md:hidden bg-[#0B0F17]/95 backdrop-blur-xl border-b border-white/[0.07] px-6 pt-2 pb-6 flex flex-col gap-1"
          >
            {LINKS.map((l) => (
              <a
                key={l.href}
                href={onHome || !l.href.startsWith("#") ? l.href : `/${l.href}`}
                data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                onClick={(e) => {
                  if (!l.href.startsWith("/")) goSection(e, l.href);
                  setOpen(false);
                }}
                className="py-3 font-mono text-xs uppercase tracking-[0.22em] text-[#D1D5DB] border-b border-white/[0.06]"
              >
                {l.label}
              </a>
            ))}
            <a
              href={onHome ? "#contact" : "/#contact"}
              data-testid="mobile-nav-hire-cta"
              onClick={(e) => {
                goSection(e, "#contact");
                setOpen(false);
              }}
              className="mt-4 text-center font-display font-semibold text-[#0B0F17] bg-[#00F0FF] rounded-full px-5 py-3"
            >
              Hire Me
            </a>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
